import { useInfiniteQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { ArrowRightLeft, Clock, Lock, Rocket, Search as SearchIcon, Trash2, X } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import type { ListTools } from "@/components/ListToolbar";
import type { MassTarget } from "@/components/MassPanel";
import { PlannedOrderMassPanel } from "@/features/planning/PlannedOrderMass";
import type { PlannedOrderMassKind } from "@/features/planning/mass";
import { DataList, DetailPanel, DetailPlaceholder, type Column } from "@/components/DataList";
import { Badge } from "@/components/ui";
import {
  convertPlannedOrders,
  coverageQuery,
  deletePlannedOrders,
  listPlannedOrders,
  planActionErrorMessage,
  planningKeys,
  plannedOrderListQuery,
  plannedStatusFilters,
  type PlannedOrderKind,
  type PlannedOrderRow,
  type PlannedStatusFilter,
} from "@/features/planning/api";
import { MassRelease } from "@/features/planning/MassRelease";
import { PlannedOrderPanel } from "@/features/planning/PlannedOrderPanel";
import { ReadinessBadge } from "@/features/planning/Readiness";
import { RunBar } from "@/features/planning/RunBar";
import { validatePlannedSearch, type PlannedSearch } from "@/features/planning/search";
import { activeCompanyOf, useActiveCompany } from "@/lib/activeCompany";
import { cn } from "@/lib/cn";
import { EXPORT_PAGE, type ExportColumn } from "@/lib/exportXlsx";
import { useFormat } from "@/lib/format";
import { sessionContextQuery, useCan } from "@/lib/session";
import { plannedOrderKinds, plannedOrderListColumns, plannedOrderReadiness, plannedOrderStatuses } from "@patronix/domain/list";
import { listView, viewQuery } from "@/lib/listView";

// Planned orders of the latest MRP run (plus firmed / converted ones). One
// keyset page per request (planned_order_page); a row opens the planned order
// (firm / delete / convert, link to the item's stock coverage, prefetched on
// hover).

export const Route = createFileRoute("/_app/planning/planned-orders")({
  validateSearch: validatePlannedSearch,
  loaderDeps: ({ search }) => ({ status: search.status, kind: search.kind, q: search.q, f: search.f, s: search.s }),
  loader: async ({ context, deps }) => {
    const ctx = await context.queryClient.ensureQueryData(sessionContextQuery);
    const companyId = activeCompanyOf(ctx)?.id;
    if (companyId) await context.queryClient.ensureInfiniteQueryData(plannedOrderListQuery(listParams(companyId, deps)));
  },
  component: PlannedOrdersPage,
});

function listParams(companyId: string, s: Pick<PlannedSearch, "status" | "kind" | "q" | "f" | "s">) {
  return { companyId, status: s.status, kind: s.kind, q: s.q, ...viewQuery(plannedOrderListColumns, s) };
}

const statusTone = { planned: "neutral", firmed: "info", converted: "success" } as const;
// LN-style sessions as tabs: planned purchase, production and subcontracting orders (ADR-055).
const kinds: (PlannedOrderKind | undefined)[] = [undefined, "purchase", "production", "subcontract"];

function PlannedOrdersPage() {
  const { t } = useTranslation();
  const fmt = useFormat();
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const queryClient = useQueryClient();
  const canManagePlans = useCan("planning.order.manage");
  const { company } = useActiveCompany();
  const companyId = company?.id ?? "";
  const setSearch = useCallback(
    (patch: Partial<PlannedSearch>) => void navigate({ search: (prev) => ({ ...prev, ...patch }), replace: true }),
    [navigate],
  );
  const params = listParams(companyId, search);
  const query = useInfiniteQuery(plannedOrderListQuery(params));
  // Keep the opened order after an action removes it from the filtered list
  // (e.g. converted), so the panel can show the result.
  const [kept, setKept] = useState<PlannedOrderRow | undefined>(undefined);
  // Mass operation open in the detail area (docs/design/mass-operations.md).
  const [massOp, setMassOp] = useState<{ kind: PlannedOrderMassKind; target: MassTarget; count: number | null } | null>(null);
  const found = query.data?.pages.flatMap((p) => p.rows).find((r) => r.id === search.po);
  if (found && found !== kept) setKept(found);
  const selected = found ?? (kept?.id === search.po ? kept : undefined);
  const [text, setText] = useState(search.q);
  useEffect(() => {
    if (text === search.q) return;
    const id = setTimeout(() => setSearch({ q: text, po: undefined }), 200);
    return () => clearTimeout(id);
  }, [text, search.q, setSearch]);

  const columns: Column<PlannedOrderRow>[] = [
    {
      key: "item",
      header: t("bom.colItem"),
      width: "minmax(200px,1.6fr)",
      cell: (r) => (
        <span className="truncate">
          <span className="font-mono text-xs">{r.item_code}</span> <span className="text-ink-soft">{r.item_description}</span>
          {r.project_code && (
            <span className="ml-1.5 rounded-md bg-(--mod-project-soft) px-1.5 py-px text-[11px] font-medium text-(--mod-project-ink)">
              {r.project_code}
            </span>
          )}
        </span>
      ),
    },
    {
      key: "kind",
      header: t("plan.colKind"),
      width: "minmax(100px,0.7fr)",
      cell: (r) => <span className="text-ink-soft">{t(`plan.kinds.${r.kind}`)}</span>,
      options: plannedOrderKinds.map((v) => ({ value: v, label: t(`plan.kinds.${v}`) })),
    },
    {
      key: "status",
      header: t("po.colStatus"),
      width: "minmax(100px,0.7fr)",
      cell: (r) => <Badge tone={statusTone[r.status]}>{t(`plan.statuses.${r.status}`)}</Badge>,
      options: plannedOrderStatuses.map((v) => ({ value: v, label: t(`plan.statuses.${v}`) })),
    },
    {
      key: "readiness",
      header: t("plan.colReadiness"),
      width: "minmax(130px,0.9fr)",
      cell: (r) =>
        (r.kind === "production" || r.kind === "subcontract") && r.status !== "converted" ? (
          <span className="flex items-center gap-1.5">
            <ReadinessBadge value={r.readiness} />
            {!!r.short_components && r.readiness !== "ready" && (
              <span className="tabular text-xs text-ink-muted" title={t("plan.shortComponentsHint")}>
                {r.short_components}
              </span>
            )}
          </span>
        ) : (
          <span className="text-ink-faint">—</span>
        ),
      options: plannedOrderReadiness.map((v) => ({ value: v, label: t(`plan.readiness.${v}`) })),
    },
    {
      key: "qty",
      header: t("prd.quantity"),
      width: "minmax(110px,0.8fr)",
      align: "end",
      cell: (r) => (
        <span className="tabular">
          {fmt.qty(String(r.qty))} <span className="text-ink-muted">{r.base_uom_code}</span>
        </span>
      ),
    },
    {
      key: "start",
      header: t("plan.colStart"),
      width: "minmax(110px,0.8fr)",
      cell: (r) => (
        <span className={cn("flex items-center gap-1.5", r.start_in_past ? "font-medium text-(--status-danger-fg)" : "text-ink-muted")}>
          {fmt.day(r.start_date)}
          {r.start_in_past && <Clock size={13} strokeWidth={2.2} aria-label={t("plan.startInPast")} role="img" />}
        </span>
      ),
    },
    {
      key: "due",
      header: t("plan.colDue"),
      width: "minmax(100px,0.7fr)",
      cell: (r) => <span className="tabular">{fmt.day(r.due_date)}</span>,
    },
    {
      key: "source",
      header: t("plan.colSupplier"),
      width: "minmax(140px,1fr)",
      cell: (r) =>
        r.kind === "purchase" || r.kind === "subcontract" ? (
          r.supplier_code ? (
            <span className="truncate">
              <span className="font-mono text-xs">{r.supplier_code}</span> <span className="text-ink-soft">{r.supplier_name}</span>
            </span>
          ) : (
            <span className="text-(--status-warning-fg)">{t("plan.noSupplier")}</span>
          )
        ) : (
          <span className="text-ink-muted">{r.warehouse_code}</span>
        ),
    },
  ];
  const exportColumns: ExportColumn<PlannedOrderRow>[] = [
    { header: t("bom.colItem"), kind: "code", value: (r) => r.item_code },
    { header: t("stock.colDescription"), kind: "text", value: (r) => r.item_description },
    { header: t("plan.colKind"), kind: "code", value: (r) => r.kind },
    { header: t("po.colStatus"), kind: "code", value: (r) => r.status },
    { header: t("prd.quantity"), kind: "number", value: (r) => r.qty, unit: (r) => r.base_uom_code },
    { header: t("items.colUom"), kind: "code", value: (r) => r.base_uom_code },
    { header: t("plan.colStart"), kind: "date", value: (r) => r.start_date },
    { header: t("plan.colDue"), kind: "date", value: (r) => r.due_date },
    { header: t("plan.colSupplier"), kind: "code", value: (r) => r.supplier_code },
    { header: t("prd.warehouse"), kind: "code", value: (r) => r.warehouse_code },
    { header: t("plan.colReadiness"), kind: "code", value: (r) => r.readiness ?? null },
    { header: t("plan.colMaterialDate"), kind: "date", value: (r) => r.material_date ?? null },
  ];
  const statusLabel = (f: PlannedStatusFilter) =>
    f === "open" ? t("po.filterOpen") : f === "all" ? t("po.filterAll") : t(`plan.statuses.${f}`);
  const chip = (active: boolean) =>
    cn(
      "h-8 rounded-full border px-3 text-[13px] transition-colors",
      active ? "border-ink bg-ink font-medium text-surface" : "border-border bg-surface text-ink hover:bg-surface-hover",
    );

  // Bulk actions on the checked suggestions: the same RPCs as the panel, one call for all ids.
  const refreshPlanning = () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: planningKeys.all }),
      queryClient.invalidateQueries({ queryKey: ["production"] }),
      queryClient.invalidateQueries({ queryKey: ["procurement"] }),
    ]);
  const openIds = (rows: PlannedOrderRow[]) => rows.filter((r) => r.status !== "converted").map((r) => r.id);
  const bulk = async (fn: (ids: string[]) => Promise<unknown>, rows: PlannedOrderRow[]) => {
    const ids = openIds(rows);
    if (!ids.length) throw new Error(t("plan.bulkNoneOpen"));
    try {
      await fn(ids);
    } catch (e) {
      throw new Error(planActionErrorMessage(t, e), { cause: e });
    }
    await refreshPlanning();
    if (search.po && ids.includes(search.po)) setSearch({ po: undefined });
  };
  const listTools: ListTools<PlannedOrderRow> = {
    id: "planned-orders",
    title: t("plan.plannedTitle"),
    onDelete: canManagePlans
      ? {
          key: "delete",
          label: t("plan.delete"),
          icon: <Trash2 size={14} />,
          danger: true,
          confirm: (count) => t("plan.bulkDeleteConfirm", { count }),
          run: (rows) => bulk(deletePlannedOrders, rows),
        }
      : undefined,
    bulk: canManagePlans
      ? [
          {
            key: "convert",
            label: t("plan.bulkConvert"),
            icon: <ArrowRightLeft size={14} />,
            confirm: (count) => t("plan.bulkConvertConfirm", { count }),
            run: (rows) => bulk(convertPlannedOrders, rows),
          },
        ]
      : [],
    mass:
      canManagePlans && companyId
        ? (
            [
              { key: "firm", label: t("mass.plannedOrders.firm"), icon: <Lock size={15} /> },
              { key: "delete", label: t("mass.plannedOrders.delete"), icon: <Trash2 size={15} /> },
            ] as const
          ).map((a) => ({
            ...a,
            open: (target: MassTarget, count: number | null) => {
              setSearch({ po: undefined, mass: undefined });
              setMassOp({ kind: a.key, target, count });
            },
          }))
        : [],
    export: { fileBase: "planned-orders", sheet: t("plan.plannedTitle"), columns: exportColumns, fetchPage: (cursor, signal) => listPlannedOrders(params, cursor, signal, EXPORT_PAGE) },
  };

  return (
    <div className="flex min-h-0 grow flex-col gap-4">
      <header className="flex flex-wrap items-end gap-3">
        <div className="flex grow flex-col gap-0.5">
          <nav aria-label="Breadcrumb" className="text-[13px] text-ink-muted">
            {t("nav.planning.title")} <span aria-hidden="true">/</span> {t("nav.planning.plannedOrders")}
          </nav>
          <h1 className="text-[28px] font-semibold leading-9 tracking-[-0.02em]">{t("plan.plannedTitle")}</h1>
        </div>
        {canManagePlans && (
          <button
            type="button"
            onClick={() => setSearch({ mass: true, po: undefined })}
            aria-pressed={!!search.mass}
            className="inline-flex h-9 items-center gap-2 rounded-xl border border-border bg-surface px-3.5 text-[13px] font-medium hover:bg-surface-hover aria-pressed:border-accent"
          >
            <Rocket size={15} aria-hidden="true" />
            {t("massRelease.open")}
          </button>
        )}
      </header>
      <RunBar companyId={companyId} />
      <div role="tablist" aria-label={t("plan.colKind")} className="-mb-1 flex gap-1 border-b border-border">
        {kinds.map((k) => (
          <button
            key={k ?? "any"}
            type="button"
            role="tab"
            aria-selected={search.kind === k}
            onClick={() => setSearch({ kind: k, po: undefined })}
            className={cn(
              "-mb-px h-9 border-b-2 px-3 text-[13px] transition-colors",
              search.kind === k ? "border-accent font-medium text-ink" : "border-transparent text-ink-muted hover:text-ink",
            )}
          >
            {k ? t(`plan.tabs.${k}`) : t("plan.allKinds")}
          </button>
        ))}
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <div role="group" aria-label={t("po.colStatus")} className="flex flex-wrap gap-2">
          {plannedStatusFilters.map((f) => (
            <button
              key={f}
              type="button"
              aria-pressed={search.status === f}
              onClick={() => setSearch({ status: f, po: undefined })}
              className={chip(search.status === f)}
            >
              {statusLabel(f)}
            </button>
          ))}
        </div>
        <div className="grow" />
        <label className="flex h-9 min-w-[240px] items-center gap-2 rounded-xl border border-border bg-surface px-3 text-ink-muted focus-within:outline-2 focus-within:outline-focus">
          <SearchIcon size={15} aria-hidden="true" />
          <input
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder={t("bom.searchPlaceholder")}
            aria-label={t("bom.searchPlaceholder")}
            data-list-search
            className="min-w-0 grow bg-transparent text-[13px] text-ink outline-none placeholder:text-ink-muted"
          />
          {text && (
            <button type="button" aria-label={t("po.clearFilters")} onClick={() => setText("")} className="text-ink-muted">
              <X size={14} />
            </button>
          )}
        </label>
      </div>
      <div className="flex min-h-0 grow gap-4">
        <DataList
          label={t("plan.plannedListLabel")}
          columns={columns}
          query={query}
          rowId={(r) => r.id}
          selectedId={search.po}
          onSelect={(r) => {
            setMassOp(null);
            setSearch({ po: r.id, mass: undefined });
          }}
          onClear={() => setSearch({ po: undefined })}
          onPrefetch={(r) => void queryClient.prefetchQuery(coverageQuery(companyId, r.item_id))}
          emptyTitle={t("plan.plannedEmptyTitle")}
          emptyBody={t("plan.plannedEmptyBody")}
          minWidth={1010}
          tools={listTools}
          view={listView(plannedOrderListColumns, search, setSearch)}
        />
        <DetailPanel
          label={massOp ? t(`mass.plannedOrders.${massOp.kind}Title`) : t("plan.plannedDetailLabel")}
          open={!!massOp || !!search.po || !!search.mass}
          placeholder={<DetailPlaceholder title={t("plan.selectPrompt")} hint={t("po.selectPromptHint")} />}
        >
          {massOp ? (
            <PlannedOrderMassPanel
              key={`${massOp.kind}:${massOp.target.kind}:${massOp.count ?? "all"}`}
              kind={massOp.kind}
              target={massOp.target}
              count={massOp.count}
              list={params}
              onClose={() => setMassOp(null)}
            />
          ) : search.mass && !search.po ? (
            <MassRelease companyId={companyId} onClose={() => setSearch({ mass: undefined })} />
          ) : selected ? (
            <PlannedOrderPanel key={selected.id} row={selected} onClose={() => setSearch({ po: undefined })} />
          ) : search.po ? (
            <p className="p-4 text-[13px] text-ink-muted">{t("notFound.body")}</p>
          ) : null}
        </DetailPanel>
      </div>
    </div>
  );
}
